/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAncestorPath } from '@squiz/dxp-console-support';
import { Breadcrumbs, BreadcrumbsProps, SPINNER_TYPE_REGULAR, Spinner } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../Constants/Strings.constants';
import { EntityAnyInsightsConfig, getInsightsService } from '../../Services/insights';
import { EntitySegmentEventSubscription, getSegmentationService } from '../../Services/segmentation';
import { createEmptyDatastoreEvent, createEmptyWebBehaviorEvent } from './EventBuilder.mocks';
import { EventBuilderProps } from './EventBuilder.props';
import { EventBuilderDatastoreForm } from './forms/EventBuilderDatastoreForm/EventBuilderDatastoreForm';
import { EventBuilderWebBehaviorForm } from './forms/EventBuilderWebBehaviorForm/EventBuilderWebBehaviorForm';

export const EVENT_BUILDER_TYPE_DATA_INTEGRATION = `data-integration`;
export const EVENT_BUILDER_TYPE_WEB_BEHAVIOR = `web-behavior`;

/**
 * The types of event builders available.
 */
export type EventBuilderType = typeof EVENT_BUILDER_TYPE_WEB_BEHAVIOR | typeof EVENT_BUILDER_TYPE_DATA_INTEGRATION;

/**
 * The create and update event screen.
 */
export const EventBuilder = (props: EventBuilderProps): JSX.Element => {
  const { type = null } = props;
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [formType, setFormType] = useState<EventBuilderType | null>(type);
  const [event, setEvent] = useState<EntityAnyInsightsConfig | EntitySegmentEventSubscription | null>(null);
  const [loadedEvent, setLoadedEvent] = useState<EntityAnyInsightsConfig | EntitySegmentEventSubscription | null>(null);

  const [segmentEvents, setSegmentEvents] = useState<Record<string, EntitySegmentEventSubscription> | null>(null);
  const [insights, setInsights] = useState<Record<string, EntityAnyInsightsConfig> | null>(null);

  // Insights service
  const insightsService = getInsightsService();
  const segmentationService = getSegmentationService();

  // Console service
  const { tenant } = useConsoleServiceStore();

  // Load initial full list of insights & events. This data is used to determine what type
  // of source an existing event belongs to and (potentially) if there are any conflicts with
  // existing event data when making changes.
  useEffect((): void => {
    lastValueFrom(insightsService.getInsights())
      .then((insights) => {
        setInsights(insights);
        if (insights && eventId && Object.keys(insights).includes(eventId)) {
          setFormType(EVENT_BUILDER_TYPE_DATA_INTEGRATION);
          setEvent(insights[eventId]);
          setLoadedEvent(insights[eventId]);
        }
        setIsLoading(false);
        return true;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    lastValueFrom(segmentationService.getEvents())
      .then((events) => {
        setSegmentEvents(events);
        if (events && eventId && Object.keys(events).includes(eventId)) {
          setFormType(EVENT_BUILDER_TYPE_WEB_BEHAVIOR);
          setEvent(events[eventId]);
          setLoadedEvent(events[eventId]);
        }
        setIsLoading(false);
        return true;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    if (!eventId) {
      if (formType === EVENT_BUILDER_TYPE_WEB_BEHAVIOR) {
        setEvent(createEmptyWebBehaviorEvent());
      } else {
        setEvent(createEmptyDatastoreEvent());
      }
      setLoadedEvent(null);
      setIsLoading(false);
    }
  }, [eventId, formType, insightsService, segmentationService]);

  // Labels
  const labels = useMemo(() => {
    return {
      createDataIntegrationEventFeed: t(CDP_STRINGS.createDataIntegrationEventFeed),
      createWebBehaviorEventFeed: t(CDP_STRINGS.createWebBehaviorEventFeed),
      defaultHeading: t(CDP_STRINGS.eventAddTitle),
      events: t(CDP_STRINGS.events),
    };
  }, [t]);

  // Prepare breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const eventsPath = `${useAncestorPath(false, 2)}/events`;
  const breadcrumbProps: BreadcrumbsProps = {
    links: [...capabilityBreadcrumbs, { name: labels.events, url: eventsPath }],
  };

  /**
   * Reset any editing changes to the event and redirect back to the base events url.
   */
  const resetChanges = useCallback(() => {
    setEvent(
      loadedEvent ||
        (formType === EVENT_BUILDER_TYPE_WEB_BEHAVIOR ? createEmptyWebBehaviorEvent() : createEmptyDatastoreEvent()),
    );
    navigate({ pathname: getCapabilityPath(`/events`) });
  }, [formType, loadedEvent, navigate]);

  if (!tenant || isLoading || event === null || insights === null || segmentEvents === null) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  let eventHeading: string | null = loadedEvent ? loadedEvent.name : null;

  let screenContent: JSX.Element | null = null;
  if (formType === EVENT_BUILDER_TYPE_WEB_BEHAVIOR) {
    if (!eventHeading) {
      eventHeading = labels.createWebBehaviorEventFeed;
    }
    screenContent = (
      <EventBuilderWebBehaviorForm
        event={event as EntitySegmentEventSubscription}
        resetChanges={resetChanges}
        setEvent={setEvent}
      />
    );
  } else {
    if (!eventHeading) {
      eventHeading = labels.createDataIntegrationEventFeed;
    }
    screenContent = (
      <EventBuilderDatastoreForm
        event={event as EntityAnyInsightsConfig}
        resetChanges={resetChanges}
        setEvent={setEvent}
      />
    );
  }

  // Breadcrumbs
  breadcrumbProps.currentPageName = eventHeading;
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="event-builder-breadcrumbs" />;

  const screenHeading = eventHeading;

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading}>
      {screenContent}
    </DxpScreen>
  );
};
